import { Clock, FileText, ImagePlus, Type, X } from "lucide-react";
import React, { ChangeEvent, useEffect, useState } from "react";
import { VideoTextItem } from "../../utils/videoGenerationRequest";
import { cn } from "../../utils/cn";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Select from "../ui/Select";
import Textarea from "../ui/Textarea";

export interface VideoGenerationFieldsProps {
  disabled?: boolean;
  compact?: boolean;
  videoPrompt: string;
  setVideoPrompt: (prompt: string) => void;
  videoDuration: number;
  setVideoDuration: (duration: number) => void;
  videoRestrictions: string;
  setVideoRestrictions: (restrictions: string) => void;
  videoProvider: string;
  setVideoProvider: (provider: string) => void;
  logoFile: File | null;
  setLogoFile: (file: File | null) => void;
  logoPreview: string | null;
  setLogoPreview: (preview: string | null) => void;
  referenceImages: File[];
  setReferenceImages: (images: File[]) => void;
  referenceImagePreviews: string[];
  setReferenceImagePreviews: (previews: string[]) => void;
  videoTexts: VideoTextItem[];
  setVideoTexts: (texts: VideoTextItem[]) => void;
  videoScript: string;
  setVideoScript: (script: string) => void;
}

const VideoGenerationFields: React.FC<VideoGenerationFieldsProps> = ({
  disabled = false,
  compact = false,
  videoPrompt,
  setVideoPrompt,
  videoDuration,
  setVideoDuration,
  videoRestrictions,
  setVideoRestrictions,
  videoProvider,
  setVideoProvider,
  logoFile,
  setLogoFile,
  logoPreview,
  setLogoPreview,
  referenceImages,
  setReferenceImages,
  referenceImagePreviews,
  setReferenceImagePreviews,
  videoTexts,
  setVideoTexts,
  videoScript,
  setVideoScript,
}) => {
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const [isDraggingReferences, setIsDraggingReferences] = useState(false);
  const supportsLogoAndReferences =
    videoProvider === "heygen" || videoProvider === "veo";

  useEffect(() => {
    if (!supportsLogoAndReferences) {
      setLogoFile(null);
      setLogoPreview(null);
      setReferenceImages([]);
      setReferenceImagePreviews([]);
    }
  }, [
    supportsLogoAndReferences,
    setLogoFile,
    setLogoPreview,
    setReferenceImages,
    setReferenceImagePreviews,
  ]);

  const processLogoFile = (file: File) => {
    if (!file.type.startsWith("image/")) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const processReferenceImages = (files: File[]) => {
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));
    if (imageFiles.length === 0) return;

    setReferenceImages([...referenceImages, ...imageFiles]);
    const newPreviews = [...referenceImagePreviews];
    imageFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result as string);
        setReferenceImagePreviews([...newPreviews]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleAddText = () => {
    setVideoTexts([
      ...videoTexts,
      {
        id: Date.now().toString(),
        text: "",
        startTime: 0,
        endTime: videoDuration || 8,
        position: "bottom-center",
      },
    ]);
  };

  return (
    <div className={cn("space-y-4", compact && "text-sm")}>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
        <p className="text-xs text-amber-800">
          <span className="font-semibold">Créditos:</span> 20 créditos
          reservados ao iniciar; estorno automático se a geração falhar.
        </p>
      </div>

      <Select
        label="Provedor de IA"
        value={videoProvider}
        onChange={setVideoProvider}
        options={[
          { value: "veo", label: "Google Veo (Recomendado)" },
          { value: "heygen", label: "HeyGen (Marketing)" },
        ]}
        size="md"
        disabled={disabled}
      />

      <Textarea
        label="Prompt do Vídeo (Opcional)"
        value={videoPrompt}
        onChange={(e) => setVideoPrompt(e.target.value)}
        placeholder="Descreva o vídeo... (vazio = usa o conteúdo do post)"
        inputSize="md"
        disabled={disabled}
      />

      <Textarea
        label="Restrições (Opcional)"
        value={videoRestrictions}
        onChange={(e) => setVideoRestrictions(e.target.value)}
        placeholder="Ex: evitar cores vibrantes, sem texto na cena..."
        inputSize="md"
        disabled={disabled}
      />

      <Input
        label="Duração (segundos)"
        type="number"
        min="1"
        max="8"
        value={videoDuration.toString()}
        onChange={(e) => {
          const duration = parseInt(e.target.value, 10) || 8;
          setVideoDuration(Math.min(Math.max(duration, 1), 8));
        }}
        inputSize="md"
        disabled={disabled}
        icon={<Clock className="h-4 w-4" />}
      />

      {supportsLogoAndReferences && (
        <>
          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-700">
              Logo (Opcional)
            </label>
            {!logoFile ? (
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDraggingLogo(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setIsDraggingLogo(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDraggingLogo(false);
                  if (e.dataTransfer.files[0]) {
                    processLogoFile(e.dataTransfer.files[0]);
                  }
                }}
                className={cn(
                  "border-2 border-dashed rounded-lg p-4 text-center",
                  isDraggingLogo
                    ? "border-purple-500 bg-purple-50"
                    : "border-purple-300",
                )}
              >
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={disabled}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      if (e.target.files?.[0])
                        processLogoFile(e.target.files[0]);
                    }}
                  />
                  <ImagePlus className="h-6 w-6 mx-auto text-gray-400" />
                  <span className="text-xs text-gray-600 block mt-2">
                    Clique ou arraste o logo
                  </span>
                </label>
              </div>
            ) : (
              <div className="relative inline-block">
                <img
                  src={logoPreview || ""}
                  alt="Logo"
                  className="w-24 h-24 object-contain border rounded-lg p-2"
                />
                <button
                  type="button"
                  onClick={() => {
                    setLogoFile(null);
                    setLogoPreview(null);
                  }}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                  disabled={disabled}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-700">
              Imagens de referência (Opcional)
            </label>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDraggingReferences(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDraggingReferences(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setIsDraggingReferences(false);
                if (e.dataTransfer.files) {
                  processReferenceImages(Array.from(e.dataTransfer.files));
                }
              }}
              className={cn(
                "border-2 border-dashed rounded-lg p-3 text-center",
                isDraggingReferences
                  ? "border-purple-500 bg-purple-50"
                  : "border-purple-300",
              )}
            >
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  disabled={disabled}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    if (e.target.files) {
                      processReferenceImages(Array.from(e.target.files));
                    }
                  }}
                />
                <ImagePlus className="h-6 w-6 mx-auto text-gray-400" />
              </label>
            </div>
            {referenceImagePreviews.length > 0 && (
              <div className="grid grid-cols-4 gap-2">
                {referenceImagePreviews.map((preview, index) => (
                  <div key={index} className="relative">
                    <img
                      src={preview}
                      alt={`Ref ${index + 1}`}
                      className="w-full aspect-square object-cover rounded-sm border"
                    />
                    <button
                      type="button"
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                      disabled={disabled}
                      onClick={() => {
                        setReferenceImages(
                          referenceImages.filter((_, i) => i !== index),
                        );
                        setReferenceImagePreviews(
                          referenceImagePreviews.filter((_, i) => i !== index),
                        );
                      }}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <div className="border border-purple-200 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Type className="h-4 w-4 text-gray-600" />
            <span className="text-sm font-semibold">Textos / overlays</span>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleAddText}
            disabled={disabled}
          >
            Adicionar
          </Button>
        </div>
        {videoTexts.map((textItem) => (
          <div
            key={textItem.id}
            className="p-3 border rounded-lg space-y-2 bg-gray-50"
          >
            <div className="flex gap-2">
              <Input
                value={textItem.text}
                onChange={(e) =>
                  setVideoTexts(
                    videoTexts.map((t) =>
                      t.id === textItem.id ? { ...t, text: e.target.value } : t,
                    ),
                  )
                }
                placeholder="Texto no vídeo"
                inputSize="sm"
                disabled={disabled}
                className="flex-1"
              />
              <button
                type="button"
                onClick={() =>
                  setVideoTexts(videoTexts.filter((t) => t.id !== textItem.id))
                }
                className="text-red-500"
                disabled={disabled}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Input
                type="number"
                value={textItem.startTime}
                onChange={(e) =>
                  setVideoTexts(
                    videoTexts.map((t) =>
                      t.id === textItem.id
                        ? {
                            ...t,
                            startTime: parseFloat(e.target.value) || 0,
                          }
                        : t,
                    ),
                  )
                }
                placeholder="Início"
                inputSize="sm"
                disabled={disabled}
              />
              <Input
                type="number"
                value={textItem.endTime}
                onChange={(e) =>
                  setVideoTexts(
                    videoTexts.map((t) =>
                      t.id === textItem.id
                        ? {
                            ...t,
                            endTime:
                              parseFloat(e.target.value) || videoDuration,
                          }
                        : t,
                    ),
                  )
                }
                placeholder="Fim"
                inputSize="sm"
                disabled={disabled}
              />
              <Select
                options={[
                  { value: "bottom-center", label: "Baixo centro" },
                  { value: "top-center", label: "Topo centro" },
                  { value: "center", label: "Centro" },
                ]}
                value={textItem.position}
                onChange={(v) =>
                  setVideoTexts(
                    videoTexts.map((t) =>
                      t.id === textItem.id ? { ...t, position: v } : t,
                    ),
                  )
                }
                size="sm"
                disabled={disabled}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="border border-purple-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="h-4 w-4 text-gray-600" />
          <span className="text-sm font-semibold">Script de narração</span>
        </div>
        <Textarea
          value={videoScript}
          onChange={(e) => setVideoScript(e.target.value)}
          placeholder="Texto narrado no vídeo (opcional)"
          inputSize="md"
          disabled={disabled}
          rows={3}
        />
      </div>
    </div>
  );
};

export default VideoGenerationFields;
